/** Reusable tenant (tour operator) picker — the console default.
 *
 * **Rows are clients** (plan D4, D10). A production operator's sandbox is folded into
 * its row; stand-alone and partner sandboxes keep a row of their own with the flask
 * "Sandbox" mark. The list endpoint does the folding (`clients=1`); a row that has a
 * sandbox carries segments to pick an environment. The franchise tree is unchanged:
 * licensees render nested under their parent (indented, `↳`, "of <parent>"), parents
 * get a dimmed "franchise"; both levels are selectable. Hierarchy comes from sorting
 * the list endpoint's `path`.
 *
 * **Single mode** (the default; form fields, workspaces): the value is one operator.
 * Segments *Production · Sandbox*; a row click picks Production (a stand-alone
 * sandbox row picks itself); ⌥↵ picks the Sandbox. The input reads
 * "Name (code)", plus "· Production" when the client has a sandbox, or
 * "· Sandbox" (with the flask) for a sandbox.
 *
 * **Multiple mode** (`multiple`; grid filters and the console scope): the value is a
 * list of operators. Every row has a tri-state checkbox (all of the client's
 * operators / one of its two / none) and segments *Production · Sandbox · Both*:
 *  - a row click (name, code or checkbox): none or one → Both; Both → none;
 *  - a segment: exactly that set; the lit one again deselects the client;
 *  - keyboard (focus stays in the input): ↵ = row click, ⇧↵ = Production only,
 *    ⌥↵ = Sandbox only; a caption footer spells the keys out;
 *  - selected clients are pinned on top under "Selected" while no search is typed;
 *  - the closed field is one line: the first client + "· Both" / "· Production" /
 *    "· Sandbox", then a "+N" count; its tooltip lists them all, ✕ clears all.
 * Internally MUI runs in SINGLE mode here (the selection model is ours): MUI's
 * `multiple` owns Backspace and ←/→ tag focus, which a one-line summary has no
 * tags for.
 *
 * **Sentinels** (`extraOptions`: System, Console only, Internal) are exclusive (D3):
 * picking one clears the tenants and vice versa. In multiple mode the value is then
 * exactly `[sentinel]` — detect it with `pickedSentinel(value, extraOptions)`.
 *
 * `featured` (single mode) lists the operators the page names first, under a "From
 * this page's filter" caption and a hairline, while no search is typed; for a client
 * with a sandbox a muted note says what the page holds ("on the page: Both"). The
 * regular list below still includes them. Create dialogs pass their `pageTenants`.
 *
 * **Closed tenants** (tenant retirement: lifecycle not `active`) are left out by the list
 * endpoint. Where listing them is useful — multiple mode (grid filters and the scope, whose key
 * footer is there anyway), or a single picker that opts in with `closedToggle` — the footer
 * carries **Show closed** (pointer, or ⌥C from the input), which asks for them too
 * (`include_closed=1`); a closed row then carries a "Closed" mark. A form field picks among
 * live tenants only, with no footer of its own. A value that names a closed tenant still
 * resolves (the `?ids=` lookup lists any operator).
 *
 * **Names.** Callers routinely reconstruct a value from a URL param alone (`{id, name:
 * ''}`). A single nameless value resolves as it always has: from the console scope,
 * else one cached `retrieve` on the shared `['tourOperator', id]` key. Clients and
 * multiple values resolve through the operator brief cache, with ONE batched `?ids=`
 * lookup for whatever the loaded list did not cover (`useTenantBriefs`); `#<id>` until
 * then, and for an id that no longer exists (still removable). Display only — what
 * `onChange` emits is `{id, name, code}` per operator.
 */
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import {
  alpha,
  Autocomplete,
  type AutocompleteChangeReason,
  type AutocompleteInputChangeReason,
  type AutocompleteRenderGroupParams,
  Box,
  CircularProgress,
  Divider,
  Paper,
  type PaperProps,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type SyntheticEvent,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as topsApi from '@/api/tourOperators';
import { visuallyHidden } from '@/components/BoolText';
import { useTenantScopeList } from '@/scope/tenantScope';

import {
  briefOf,
  type ClientEnv,
  type ClientSelection,
  clientOfBrief,
  clientsForIds,
  clientState,
  type ClientState,
  groupSelection,
  pickClientEnv,
  summarizeSelection,
  type TenantBrief,
  type TenantClient,
  toggleClient,
} from './clients';
import { seedTenantBriefs, useTenantBriefs } from './useTenantBriefs';

const SEARCH_DEBOUNCE_MS = 200;
const PAGE_SIZE = 50;
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const FEATURED_CAPTION = "From this page's filter";
const SELECTED_CAPTION = 'Selected';

/** Minimal value shape — callers may store just {id, name}. */
export interface TenantPickerValue {
  id: number;
  name: string;
  code?: string;
}

/** The sentinel a multiple picker's value holds, if any: the value is exactly
 *  `[sentinel]` (sentinels are exclusive, D3). */
export function pickedSentinel(
  value: readonly TenantPickerValue[],
  extraOptions: readonly TenantPickerValue[] | undefined,
): TenantPickerValue | null {
  const only = value.length === 1 ? value[0] : undefined;
  return only && extraOptions?.some((x) => x.id === only.id) ? only : null;
}

type Group = 'selected' | 'featured' | 'all';

/** One option: a client row (or a sentinel). */
interface Row extends TenantClient {
  /** Unique across groups — a featured/pinned client is listed twice. */
  key: string;
  group: Group;
  depth: 0 | 1;
  isFranchise?: boolean;
  /** Parent context for licensees. */
  parentName?: string | null;
  /** Sentinel rows from `extraOptions` (italic, no code). */
  sentinel?: boolean;
  /** A closed tenant (listed with Show closed): its own lifecycle is not `active`. */
  closed?: boolean;
  /** No operator data: `#id`, or the name the caller gave. */
  unresolved?: boolean;
  /** A featured client's environment on the page ("on the page: Both"). */
  note?: string;
  /** Single mode's value: the input text. */
  label?: string;
  /** Single mode's value: the operator picked within the client. */
  pickedId?: number;
}

/** The value MUI holds in multiple mode, always: a stable token whose label is empty
 *  (the field shows our one-line summary instead) and which "equals" every option
 *  that is picked, so MUI marks those `aria-selected`. Never null, so MUI does not
 *  re-sync its highlight when the first client is picked or the last one dropped. */
const SUMMARY: Row = {
  key: 'summary',
  group: 'all',
  depth: 0,
  id: -1,
  name: '',
  code: '',
  sandbox: null,
  standaloneSandbox: false,
};

const ENV_WORD: Record<ClientEnv, string> = { production: 'Production', sandbox: 'Sandbox', both: 'Both' };

/** Order a flat search result hierarchically (sort by `path`, so licensees follow
 *  their parent) and fold each production operator's sandbox into its row. With
 *  `clients=1` the server already did the folding; a sandbox row whose production
 *  operator is listed too (an older backend) rides on that row all the same. */
function arrange(items: readonly topsApi.TourOperatorListItem[]): Row[] {
  const sorted = [...items].sort((a, b) => a.path.join(' ').localeCompare(b.path.join(' ')));
  const listed = new Set(items.map((t) => t.id));
  const foldedSandbox = new Map<number, topsApi.TourOperatorPairRef>();
  for (const t of items) {
    const b = briefOf(t);
    if (b.sandboxOf && listed.has(b.sandboxOf.id)) foldedSandbox.set(b.sandboxOf.id, { id: b.id, code: b.code, name: b.name });
  }
  const rows: Row[] = [];
  const seen = new Set<number>();
  for (const t of sorted) {
    const b = briefOf(t);
    if (b.sandboxOf && listed.has(b.sandboxOf.id)) continue;
    const client = clientOfBrief(b);
    if (seen.has(client.id)) continue;
    seen.add(client.id);
    const own = client.id === t.id;
    rows.push({
      ...client,
      sandbox: client.sandbox ?? foldedSandbox.get(client.id) ?? null,
      key: `all:c:${client.id}`,
      group: 'all',
      depth: own && t.parent_id ? 1 : 0,
      isFranchise: own && t.is_franchise,
      parentName: own ? t.parent_name : null,
      ...(own && t.lifecycle && t.lifecycle !== 'active' ? { closed: true } : {}),
    });
  }
  return rows;
}

const sentinelRow = (x: TenantPickerValue, group: Group): Row => ({
  id: x.id,
  name: x.name,
  code: x.code ?? '',
  sandbox: null,
  standaloneSandbox: false,
  key: `${group}:x:${x.id}`,
  group,
  depth: 0,
  sentinel: true,
});

const selectionRow = (s: Pick<ClientSelection, 'client' | 'unresolved'>, group: Group, note?: string): Row => ({
  ...s.client,
  key: `${group}:${s.unresolved ? 'u' : 'c'}:${s.client.id}`,
  group,
  depth: 0,
  ...(s.unresolved ? { unresolved: true } : {}),
  ...(note ? { note } : {}),
});

const sameRow = (a: Row, b: Row) => a.sentinel === b.sentinel && a.id === b.id;

/** What the loaded rows say about an operator — synchronous, unlike the brief cache,
 *  whose observers hear of a seed one tick after the rows render. */
function briefFromRows(rows: readonly Row[], id: number): TenantBrief | undefined {
  for (const r of rows) {
    if (r.sentinel) continue;
    if (r.id === id) {
      return {
        id,
        name: r.name,
        code: r.code,
        environment: r.standaloneSandbox ? 'sandbox' : 'production',
        sandbox: r.sandbox,
        sandboxOf: null,
      };
    }
    if (r.sandbox?.id === id) {
      return {
        id,
        name: r.sandbox.name,
        code: r.sandbox.code,
        environment: 'sandbox',
        sandbox: null,
        sandboxOf: { id: r.id, code: r.code, name: r.name },
      };
    }
  }
  return undefined;
}

/** The operator `id` of a client, as a value. */
function operatorValue(row: Row, id: number): TenantPickerValue {
  if (row.sandbox && id === row.sandbox.id) {
    return { id, name: row.sandbox.name, code: row.sandbox.code };
  }
  return { id: row.id, name: row.unresolved ? '' : row.name, code: row.code || undefined };
}

/** What the option's accessible name says. Single mode names plain rows by their
 *  text, as before; a row with a sandbox (or any row in multiple mode) says what the
 *  segments and the checkbox show. */
function rowAriaName(row: Row, state: ClientState, multiple: boolean): string | undefined {
  if (!multiple && !row.sandbox) return undefined;
  const parts = [row.name];
  if (row.code) parts.push(row.code);
  if (row.depth === 1 && row.parentName) parts.push(`of ${row.parentName}`);
  if (row.depth === 0 && row.isFranchise) parts.push('franchise');
  if (row.sandbox) parts.push('has a sandbox');
  if (row.standaloneSandbox) parts.push('a sandbox');
  if (row.closed) parts.push('closed');
  if (row.note) parts.push(row.note);
  const head = parts.join(', ');
  if (state === 'none') return multiple ? `${head}; not selected` : head;
  if (!row.sandbox) return `${head}; selected`;
  const words = state === 'both' ? 'production and sandbox' : state;
  return `${head}; selected: ${words}`;
}

// ── the popup's caption footer (keys), rendered inside MUI's Paper slot ──────────

const FooterContext = createContext<ReactNode>(null);

function PickerPaper({ children, ...props }: PaperProps) {
  const footer = useContext(FooterContext);
  return (
    <Paper {...props}>
      {children}
      {footer}
    </Paper>
  );
}

const PICKER_SLOTS = { paper: PickerPaper };

const KEY_WORDS: Record<string, string> = { '↵': 'Enter', '⇧': 'Shift', '⌥': 'Option', Esc: 'Escape' };

function KeyHint({ keys, action }: { keys: string[]; action: string }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {keys.map((k) => (
        <Box
          key={k}
          component="kbd"
          aria-hidden
          sx={{
            fontFamily: 'inherit',
            fontSize: 11,
            fontWeight: 600,
            lineHeight: '16px',
            px: 0.625,
            border: 1,
            borderBottomWidth: 2,
            borderColor: 'divider',
            borderRadius: 0.5,
            bgcolor: 'background.default',
            color: 'text.primary',
          }}
        >
          {k}
        </Box>
      ))}
      <Box component="span" sx={visuallyHidden}>
        {keys.map((k) => KEY_WORDS[k] ?? k).join('+')}
      </Box>
      {action}
    </Box>
  );
}

// ── row pieces ───────────────────────────────────────────────────────────────

/** "· Both" / "· Production" / "· ⚗ Sandbox" after a client's name (the closed field,
 *  the scope chip). Muted; the flask always has its word. */
export function EnvWord({ word, sandbox }: { word: string; sandbox: boolean }) {
  return (
    <Box
      component="span"
      sx={{ flex: 'none', color: 'inherit', opacity: 0.72, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      <span aria-hidden>·</span>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
        {sandbox && <ScienceOutlinedIcon aria-hidden sx={{ fontSize: '1em' }} />}
        {word}
      </Box>
    </Box>
  );
}

function TriState({ state }: { state: 'all' | 'some' | 'none' }) {
  const sx = { fontSize: 20, flex: 'none', color: state === 'none' ? 'action.active' : 'primary.main' };
  if (state === 'all') return <CheckBoxIcon aria-hidden sx={sx} data-tri="checked" />;
  if (state === 'some') return <IndeterminateCheckBoxIcon aria-hidden sx={sx} data-tri="indeterminate" />;
  return <CheckBoxOutlineBlankIcon aria-hidden sx={sx} data-tri="unchecked" />;
}

const captionSx = { fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap' } as const;

function SandboxMark() {
  return (
    <Box
      component="span"
      sx={{
        alignSelf: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.375,
        fontSize: 12,
        lineHeight: '18px',
        color: 'text.secondary',
        border: 1,
        borderColor: 'divider',
        borderRadius: 999,
        pl: 0.625,
        pr: 0.875,
        whiteSpace: 'nowrap',
      }}
    >
      <ScienceOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
      Sandbox
    </Box>
  );
}

/** A closed tenant's row mark: the stop icon and the word, in the Sandbox mark's shape. */
function ClosedMark() {
  return (
    <Box
      component="span"
      data-closed-mark
      sx={{
        alignSelf: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.375,
        fontSize: 12,
        lineHeight: '18px',
        color: 'text.secondary',
        border: 1,
        borderColor: 'divider',
        borderRadius: 999,
        pl: 0.625,
        pr: 0.875,
        whiteSpace: 'nowrap',
      }}
    >
      <DoNotDisturbOnOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
      Closed
    </Box>
  );
}

/** The footer's Show closed switch: a pointer affordance that never takes focus from the input
 *  (⌥C from the input does the same; `aria-keyshortcuts` says so). */
function ClosedToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      tabIndex={-1}
      aria-pressed={on}
      aria-label="Show closed tenants"
      aria-keyshortcuts="Alt+C"
      data-picker-closed
      onMouseDown={(e: ReactMouseEvent) => e.preventDefault()}
      onClick={(e: ReactMouseEvent) => {
        e.stopPropagation();
        onToggle();
      }}
      sx={{
        font: 'inherit',
        fontSize: 12,
        lineHeight: '18px',
        ml: 'auto',
        p: 0,
        border: 0,
        bgcolor: 'transparent',
        cursor: 'pointer',
        color: on ? 'primary.main' : 'text.secondary',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        whiteSpace: 'nowrap',
        '&:hover': { color: 'text.primary' },
      }}
    >
      {on ? (
        <CheckBoxIcon aria-hidden sx={{ fontSize: 16 }} />
      ) : (
        <CheckBoxOutlineBlankIcon aria-hidden sx={{ fontSize: 16 }} />
      )}
      Show closed
      <Box
        component="kbd"
        aria-hidden
        sx={{
          fontFamily: 'inherit',
          fontSize: 11,
          fontWeight: 600,
          lineHeight: '16px',
          px: 0.625,
          border: 1,
          borderBottomWidth: 2,
          borderColor: 'divider',
          borderRadius: 0.5,
          bgcolor: 'background.default',
          color: 'text.primary',
        }}
      >
        ⌥C
      </Box>
    </Box>
  );
}

/** *Production · Sandbox (· Both)*: pointer affordances. The option's accessible name
 *  and the keyboard (⇧↵ / ⌥↵) carry the same choices, so the segments stay out of
 *  the tab order and the accessibility tree, and a mousedown never blurs the input. */
function Segments({
  envs,
  current,
  onPick,
}: {
  envs: ClientEnv[];
  current: ClientState;
  onPick: (env: ClientEnv) => void;
}) {
  return (
    <Box
      component="span"
      aria-hidden
      data-segments
      sx={{ flex: 'none', display: 'inline-flex', border: 1, borderColor: 'divider', borderRadius: '6px', overflow: 'hidden' }}
    >
      {envs.map((env) => (
        <Box
          key={env}
          component="button"
          type="button"
          tabIndex={-1}
          aria-pressed={current === env}
          onMouseDown={(e: ReactMouseEvent) => e.preventDefault()}
          onClick={(e: ReactMouseEvent) => {
            e.stopPropagation();
            onPick(env);
          }}
          sx={{
            font: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '20px',
            height: 22,
            px: '9px',
            m: 0,
            border: 0,
            cursor: 'pointer',
            color: 'text.secondary',
            bgcolor: 'background.paper',
            '& + &': { borderLeft: 1, borderColor: 'divider' },
            '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.04), color: 'text.primary' },
            '&[aria-pressed="true"]': { bgcolor: 'primary.main', color: 'primary.contrastText' },
            '&[aria-pressed="true"] + &': { borderLeftColor: 'primary.main' },
            '@media (prefers-reduced-motion: no-preference)': {
              transition: 'background-color 150ms ease, color 150ms ease',
            },
          }}
        >
          {ENV_WORD[env]}
        </Box>
      ))}
    </Box>
  );
}

// ── label recovery for a single value ─────────────────────────────────────────

function useTenantLabel(value: TenantPickerValue | null) {
  const scopeList = useTenantScopeList();
  const nameless = value !== null && !value.name;
  const scoped = nameless ? scopeList.find((s) => s.id === value.id) : undefined;
  const enabled = nameless && !scoped;
  const q = useQuery({
    queryKey: ['tourOperator', value?.id],
    queryFn: () => topsApi.retrieve(value?.id as number),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
  const detail = q.data && value && q.data.id === value.id ? q.data : undefined;
  const display = useMemo<TenantPickerValue | null>(() => {
    if (!value || value.name) return value;
    if (scoped) return scoped;
    if (detail) return { id: detail.id, name: detail.name, ...(detail.code ? { code: detail.code } : {}) };
    // Still loading (or gone): an id beats a blank box.
    return { ...value, name: `#${value.id}` };
  }, [value, scoped, detail]);
  return { display, detail, pending: enabled && q.status === 'pending' };
}

/** `value` with its name filled in, for display: from the console scope when the
 *  ids match, otherwise one cached `retrieve` on the shared `['tourOperator', id]`
 *  key; `#<id>` until it answers. A named value passes through. Callers that show a
 *  nameless page tenant outside the picker (a wizard's summary) read the same cache. */
export function useResolvedTenant(value: TenantPickerValue | null): TenantPickerValue | null {
  return useTenantLabel(value).display;
}

/** A selection's clients, for summaries outside the picker (the AppBar scope chip, a
 *  grid's empty state): briefs from the shared cache, `lookup` to batch-fetch the
 *  missing ones (`?ids=`). */
export function useTenantSelection(
  values: readonly TenantPickerValue[],
  { lookup = true }: { lookup?: boolean } = {},
): ClientSelection[] {
  const ids = values.map((v) => v.id);
  const briefs = useTenantBriefs(ids, { enabled: lookup });
  return useMemo(
    () =>
      groupSelection(
        ids,
        (id) => briefs.get(id),
        (id) => values.find((v) => v.id === id),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [briefs, values.map((v) => `${v.id}:${v.name}:${v.code ?? ''}`).join(',')],
  );
}

// ── the picker ────────────────────────────────────────────────────────────────

interface CommonProps {
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  /** Sentinel entries pinned before the tenant list (e.g. "Console only"). Exclusive
   *  with the tenants (D3). */
  extraOptions?: TenantPickerValue[];
  /** Dropdown panel width — wider than the input so full names fit. */
  popupWidth?: number;
  /** Dropdown z-index override — needed when the picker sits inside an
   *  elevated Popper (e.g. TagConditionInput's builder panel), whose z-index
   *  exceeds the Autocomplete popper default (`zIndex.modal`). */
  popperZIndex?: number;
  /** Restrict to ROOT operators (franchise parents + standalone TOs, i.e.
   *  `parent_id IS NULL`) — the set that can own parent-scoped resources like
   *  trip types. Licensees are excluded. Default: all operators. */
  rootsOnly?: boolean;
  /** Offer Show closed (closed tenants listed on request, ⌥C). Default: on in multiple mode
   *  (lookups: grid filters, the scope), off for a single form field — a closed tenant is
   *  refused everywhere, so a form has nothing to pick among them. */
  closedToggle?: boolean;
}

export interface TenantPickerSingleProps extends CommonProps {
  multiple?: false;
  value: TenantPickerValue | null;
  /** Emits the selected operator (or null on clear). */
  onChange: (tenant: TenantPickerValue | null) => void;
  /** Operators listed first, under "From this page's filter" and a hairline,
   *  while no search is typed (the page's tenants, for a create dialog). They
   *  stay in the regular list too. Nameless values resolve like `value`. */
  featured?: TenantPickerValue[];
}

export interface TenantPickerMultipleProps extends CommonProps {
  multiple: true;
  /** The picked operators (or exactly `[sentinel]`). */
  value: readonly TenantPickerValue[];
  /** Emits the picked operators, sorted by id — a client's *Both* is two entries. */
  onChange: (next: TenantPickerValue[]) => void;
  /** Width of the closed field, which never grows with the selection (default 280). */
  fieldWidth?: number | string;
}

export type TenantPickerProps = TenantPickerSingleProps | TenantPickerMultipleProps;

export function TenantPicker(props: TenantPickerMultipleProps): JSX.Element;
export function TenantPicker(props: TenantPickerSingleProps): JSX.Element;
export function TenantPicker(props: TenantPickerProps): JSX.Element {
  const {
    label = 'Tenant',
    placeholder = 'All tenants',
    size = 'small',
    extraOptions,
    popupWidth = 480,
    popperZIndex,
    rootsOnly = false,
  } = props;
  const multiple = props.multiple === true;
  const closedToggle = props.closedToggle ?? multiple;
  const qc = useQueryClient();
  const scopeList = useTenantScopeList();

  const [input, setInput] = useState('');
  // A search the user typed — not the label MUI writes into the input for a
  // selected value, which is no search at all.
  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);
  /** The search the loaded options answer (null before the first list lands). */
  const [loadedQuery, setLoadedQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<Row | null>(null);
  // Closed tenants are left out of the list unless asked (Show closed, where it is offered).
  const [showClosed, setShowClosed] = useState(false);
  const includeClosed = closedToggle && showClosed;
  // Multiple: the clients pinned under "Selected" are the ones picked when the popup
  // opened (or when a search was cleared), so rows never jump under the pointer or
  // the keyboard highlight while you pick; a client unpicked here stays pinned.
  const [pinSnapshot, setPinSnapshot] = useState<{ ids: number[]; sentinelId: number | null } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const captionId = useId();
  const footerId = useId();
  const summaryId = useId();

  // The server search runs on what the user TYPED. The label MUI writes into the
  // input for a selected value is no search: sent as one, it matches nothing
  // ("Acme Tours (ACM)") and the regular list read "No options" after a pick or
  // a dialog's seed.
  const query = searching ? input.trim() : '';

  useEffect(() => {
    const trimmed = query;
    const t = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const r = await topsApi.list({
          search: trimmed,
          pageSize: PAGE_SIZE,
          clients: true,
          ...(rootsOnly ? { parent_id: 'null' as const } : {}),
          ...(includeClosed ? { include_closed: true } : {}),
        });
        if (ctrl.signal.aborted) return;
        seedTenantBriefs(qc, r.results);
        setOptions(arrange(r.results));
      } catch {
        // aborts / transient network errors — the next keystroke retries
      } finally {
        if (!ctrl.signal.aborted) {
          setLoading(false);
          setListLoaded(true);
          setLoadedQuery(trimmed);
        }
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, rootsOnly, includeClosed, qc]);

  const sentinels = extraOptions ?? [];
  const sentinelRows = sentinels
    .filter((x) => !query || x.name.toLowerCase().includes(query.toLowerCase()))
    .map((x) => sentinelRow(x, 'all'));

  // ── the value ──────────────────────────────────────────────────────────────
  const values: readonly TenantPickerValue[] = props.multiple
    ? props.value
    : props.value
      ? [props.value]
      : [];
  const sentinel = props.multiple
    ? pickedSentinel(props.value, sentinels)
    : props.value && sentinels.some((x) => x.id === props.value?.id)
      ? props.value
      : null;
  const valueIds = sentinel ? [] : values.map((v) => v.id);
  const selectedSet = new Set(valueIds);
  const latestPick = useRef({ ids: valueIds, sentinelId: sentinel?.id ?? null });
  latestPick.current = { ids: valueIds, sentinelId: sentinel?.id ?? null };
  useEffect(() => {
    if (!searching) setPinSnapshot((snap) => (snap ? latestPick.current : snap));
  }, [searching]);

  // A single nameless value resolves as it always has (scope, else `retrieve`).
  const single = !props.multiple && !sentinel ? props.value : null;
  const labelLookup = useTenantLabel(single);

  // Featured operators (single mode): names from the scope, else `retrieve`.
  const featuredList = (!props.multiple && props.featured) || [];
  const featuredLookups = useQueries({
    queries: featuredList.map((f) => ({
      queryKey: ['tourOperator', f.id],
      queryFn: () => topsApi.retrieve(f.id),
      enabled: !f.name && !scopeList.some((s) => s.id === f.id),
      staleTime: Infinity,
      retry: false,
    })),
  });

  const details = new Map<number, TenantBrief>();
  if (labelLookup.detail) details.set(labelLookup.detail.id, briefOf(labelLookup.detail));
  featuredLookups.forEach((q, i) => {
    const f = featuredList[i];
    if (f && q.data && q.data.id === f.id) details.set(f.id, briefOf(q.data));
  });
  const detailsPending =
    labelLookup.pending ||
    featuredLookups.some((q, i) => {
      const f = featuredList[i];
      return !!f && !f.name && !scopeList.some((s) => s.id === f.id) && q.status === 'pending';
    });

  // Clients for the value and the featured operators: the brief cache (which every
  // list response seeds), the detail lookups, then one batched `?ids=` lookup —
  // once the first list has landed, so the ids it covers are never fetched twice.
  const briefIds = [...valueIds, ...(pinSnapshot?.ids ?? []), ...featuredList.map((f) => f.id)];
  const known = new Set(details.keys());
  for (const id of briefIds) if (briefFromRows(options, id)) known.add(id);
  const briefs = useTenantBriefs(briefIds, { enabled: listLoaded && !detailsPending, known });
  const briefFor = (id: number): TenantBrief | null | undefined =>
    details.get(id) ?? briefFromRows(options, id) ?? briefs.get(id);

  /** The name a caller, the scope or the loaded list gives an id. */
  const nameOf = (id: number): TenantPickerValue | null => {
    const v = values.find((x) => x.id === id && x.name) ?? featuredList.find((x) => x.id === id && x.name);
    if (v) return v;
    const s = scopeList.find((x) => x.id === id);
    if (s) return s;
    if (labelLookup.display && labelLookup.display.id === id) return labelLookup.display;
    return null;
  };

  /** `{id, name, code}` for an operator id, for what `onChange` emits. */
  const valueOfId = (id: number): TenantPickerValue => {
    const b = briefFor(id);
    if (b) return { id, name: b.name, ...(b.code ? { code: b.code } : {}) };
    const n = nameOf(id);
    return n && !n.name.startsWith('#') ? { id, name: n.name, ...(n.code ? { code: n.code } : {}) } : { id, name: '' };
  };

  // ── multiple: the selection, pinned on top ─────────────────────────────────
  const selection: ClientSelection[] = multiple && !sentinel ? groupSelection(valueIds, briefFor, nameOf) : [];
  const summary = multiple ? summarizeSelection(selection) : null;

  // ── single: the value as a client row (memoised: MUI resets the input text
  //    whenever the value's identity changes) ───────────────────────────────────
  let valueSig = '';
  let singleRow: Row | null = null;
  if (!props.multiple && props.value) {
    if (sentinel) {
      singleRow = sentinelRow(sentinel, 'all');
      valueSig = `x:${sentinel.id}:${sentinel.name}`;
    } else if (labelLookup.display) {
      const shown = labelLookup.display;
      const brief = briefFor(shown.id);
      const client: TenantClient = brief
        ? clientOfBrief(brief)
        : { id: shown.id, name: shown.name, code: shown.code ?? '', sandbox: null, standaloneSandbox: false };
      const isSandbox = brief?.environment === 'sandbox';
      const env = isSandbox ? 'Sandbox' : client.sandbox ? 'Production' : null;
      const name = isSandbox && client.sandbox ? client.name : shown.name;
      const code = shown.code ?? (brief?.code || '');
      const text = `${code ? `${name} (${code})` : name}${env ? ` · ${env}` : ''}`;
      singleRow = { ...client, key: `value:${client.id}`, group: 'all', depth: 0, label: text, pickedId: shown.id };
      valueSig = `c:${client.id}:${shown.id}:${text}:${client.sandbox?.id ?? ''}`;
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const valueRow = useMemo(() => singleRow, [valueSig]);
  const valueIsSandbox =
    !!valueRow && !valueRow.sentinel && (valueRow.standaloneSandbox || valueRow.pickedId === valueRow.sandbox?.id);

  /** Where a row's client stands in the current value. */
  const stateOf = (row: Row): ClientState => {
    if (multiple) {
      if (row.sentinel) return sentinel?.id === row.id ? 'production' : 'none';
      return clientState(row, selectedSet);
    }
    if (!valueRow || !sameRow(row, valueRow)) return 'none';
    if (row.sentinel) return 'production';
    return valueIsSandbox ? 'sandbox' : 'production';
  };

  // ── options ────────────────────────────────────────────────────────────────
  const allRows = [...sentinelRows, ...options];
  let firstGroup: Row[] = [];
  if (!searching) {
    if (multiple) {
      const snap = pinSnapshot ?? latestPick.current;
      const pinnedSentinel = snap.sentinelId === null ? undefined : sentinels.find((x) => x.id === snap.sentinelId);
      firstGroup = pinnedSentinel
        ? [sentinelRow(pinnedSentinel, 'selected')]
        : clientsForIds(snap.ids, briefFor, nameOf).map((c) => selectionRow(c, 'selected'));
    } else if (featuredList.length) {
      // A featured client with a sandbox says what the page holds of it.
      firstGroup = groupSelection(
        featuredList.map((f) => f.id),
        briefFor,
        nameOf,
      ).map((s) => selectionRow(s, 'featured', s.client.sandbox ? `on the page: ${ENV_WORD[s.state]}` : undefined));
    }
  }
  const pinned = new Set(firstGroup.map((r) => `${r.sentinel ? 'x' : 'c'}:${r.id}`));
  const rest =
    multiple && firstGroup.length
      ? allRows.filter((r) => !pinned.has(`${r.sentinel ? 'x' : 'c'}:${r.id}`))
      : allRows;
  const grouped = firstGroup.length > 0;
  const muiOptions = grouped ? [...firstGroup, ...rest] : rest;
  const anySandbox = muiOptions.some((r) => r.sandbox);

  // ── changes ────────────────────────────────────────────────────────────────
  const emitIds = (ids: number[]) => {
    if (props.multiple) props.onChange(ids.map(valueOfId));
  };

  const onSegment = (row: Row, env: ClientEnv) => {
    if (props.multiple) {
      emitIds(pickClientEnv(valueIds, row, env));
      return;
    }
    props.onChange(operatorValue(row, env === 'sandbox' && row.sandbox ? row.sandbox.id : row.id));
    setOpen(false);
  };

  const handleChange = (event: SyntheticEvent, option: Row | null, reason: AutocompleteChangeReason) => {
    if (reason === 'clear' || option === null || option === SUMMARY) {
      if (props.multiple) props.onChange([]);
      else props.onChange(null);
      return;
    }
    const kb = event.type === 'keydown' ? (event as ReactKeyboardEvent) : null;
    const env: ClientEnv | null =
      kb && option.sandbox ? (kb.shiftKey ? 'production' : kb.altKey ? 'sandbox' : null) : null;
    if (props.multiple) {
      if (option.sentinel) {
        props.onChange(sentinel?.id === option.id ? [] : [{ id: option.id, name: option.name, ...(option.code ? { code: option.code } : {}) }]);
        return;
      }
      emitIds(env ? pickClientEnv(valueIds, option, env) : toggleClient(valueIds, option));
      return;
    }
    if (option.sentinel) {
      props.onChange({ id: option.id, name: option.name, code: option.code || undefined });
      return;
    }
    props.onChange(operatorValue(option, env === 'sandbox' && option.sandbox ? option.sandbox.id : option.id));
  };

  const handleInputChange = (_: SyntheticEvent, v: string, reason: AutocompleteInputChangeReason) => {
    // Multiple: a pick keeps what the user typed; MUI's resets carry an empty label.
    if (multiple && (reason === 'reset' || reason === 'selectOption' || reason === 'removeOption')) return;
    setInput(v);
    setSearching(reason === 'input' && v.trim() !== '');
  };

  // ── rendering ──────────────────────────────────────────────────────────────
  const renderGroup = (params: AutocompleteRenderGroupParams) =>
    params.group === 'selected' || params.group === 'featured' ? (
      <li key={params.key} role="presentation">
        <Typography
          id={captionId}
          component="div"
          sx={{ fontSize: 12, fontWeight: 600, lineHeight: '28px', color: 'text.secondary', px: 2 }}
        >
          {params.group === 'selected' ? SELECTED_CAPTION : FEATURED_CAPTION}
        </Typography>
        <Box component="ul" role="group" aria-labelledby={captionId} sx={{ p: 0 }}>
          {params.children}
        </Box>
      </li>
    ) : (
      <li key={params.key} role="presentation">
        <Divider sx={{ my: 0.5 }} />
        <Box component="ul" role="presentation" sx={{ p: 0 }}>
          {params.children}
        </Box>
      </li>
    );

  const footerRow = highlighted ? (muiOptions.find((r) => r.key === highlighted.key) ?? null) : null;
  const keyHints =
    open && (multiple || anySandbox) ? (
      <Box
        id={footerId}
        data-picker-keys
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          columnGap: 1.75,
          rowGap: 0.5,
        }}
      >
        {footerRow?.sandbox ? (
          multiple ? (
            <>
              <KeyHint keys={['↵']} action="both" />
              <KeyHint keys={['⇧', '↵']} action="production" />
              <KeyHint keys={['⌥', '↵']} action="sandbox" />
            </>
          ) : (
            <>
              <KeyHint keys={['↵']} action="production" />
              <KeyHint keys={['⌥', '↵']} action="sandbox" />
            </>
          )
        ) : (
          <>
            <KeyHint keys={['↵']} action="select" />
            <KeyHint keys={['Esc']} action="close" />
          </>
        )}
      </Box>
    ) : null;
  const footer = open && (keyHints || closedToggle) ? (
    <Box
      data-picker-footer
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        px: 1.75,
        py: 0.875,
        fontSize: 12,
        lineHeight: '18px',
        color: 'text.secondary',
        display: 'flex',
        alignItems: 'flex-start',
        columnGap: 1.75,
      }}
    >
      {keyHints}
      {closedToggle && <ClosedToggle on={includeClosed} onToggle={() => setShowClosed((v) => !v)} />}
    </Box>
  ) : null;

  const hasSelection = multiple && (selection.length > 0 || sentinel !== null);
  const summaryText = sentinel ? sentinel.name : (summary?.full ?? '');
  const summaryNode =
    multiple && input === '' && hasSelection ? (
      <Tooltip title={summaryText} describeChild>
        <Box
          component="span"
          data-picker-summary
          // The hidden full list below describes the input; this line is cut.
          aria-hidden
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            minWidth: 0,
            flex: '0 1 auto',
            whiteSpace: 'nowrap',
            pl: 0.5,
            // body2, the field text of DESIGN.md: the toolbar field is ~280px.
            fontSize: 14,
          }}
        >
          {sentinel ? (
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontStyle: 'italic' }}>
              {sentinel.name}
            </Box>
          ) : (
            summary && (
              <>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {summary.first.client.name}
                </Box>
                {summary.env && <EnvWord sandbox={summary.first.state === 'sandbox'} word={summary.env} />}
                {summary.more > 0 && (
                  <Box
                    component="span"
                    sx={{
                      flex: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: '18px',
                      color: 'primary.main',
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                      borderRadius: 999,
                      px: 1,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    +{summary.more}
                  </Box>
                )}
              </>
            )
          )}
        </Box>
      </Tooltip>
    ) : null;

  const describedBy = [summaryNode ? summaryId : null, keyHints ? footerId : null].filter(Boolean).join(' ') || undefined;

  return (
    <FooterContext.Provider value={footer}>
      <Autocomplete<Row, false, boolean, false>
        size={size}
        open={open}
        onOpen={() => {
          setPinSnapshot(latestPick.current);
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
          setPinSnapshot(null);
        }}
        options={muiOptions}
        value={multiple ? SUMMARY : valueRow}
        onChange={handleChange}
        inputValue={input}
        onInputChange={handleInputChange}
        onHighlightChange={(_, o) => setHighlighted(o)}
        // ⌥C: Show closed, without leaving the input (the footer's switch, for the keyboard).
        onKeyDown={(e) => {
          if (closedToggle && e.altKey && !e.metaKey && !e.ctrlKey && e.code === 'KeyC') {
            e.preventDefault();
            (e as ReactKeyboardEvent & { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
            setShowClosed((v) => !v);
          }
        }}
        getOptionLabel={(t) => (t === SUMMARY ? '' : (t.label ?? (t.code ? `${t.name} (${t.code})` : t.name)))}
        getOptionKey={(t) => t.key}
        groupBy={grouped ? (t) => t.group : undefined}
        renderGroup={grouped ? renderGroup : undefined}
        // Server-side search is authoritative (matches code/name/email).
        filterOptions={(opts) => opts}
        isOptionEqualToValue={(o, v) => (v === SUMMARY ? stateOf(o) !== 'none' : sameRow(o, v))}
        disableCloseOnSelect={multiple}
        clearText={multiple ? 'Clear all tenants' : 'Clear'}
        // Until the answer to THIS search lands (the debounce, the request), the popup
        // says Loading… — never "No tenants match" for a search not yet asked.
        {...(query ? { noOptionsText: `No tenants match “${query}”` } : {})}
        loading={loading || (open && loadedQuery !== query)}
        slots={PICKER_SLOTS}
        sx={
          multiple
            ? {
                width: props.multiple ? (props.fieldWidth ?? 280) : undefined,
                maxWidth: '100%',
                '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                // The summary gets the room; the caret still has a sliver to sit in.
                ...(summaryNode ? { '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 4 } } : {}),
                // ✕ clears all — only while something is picked.
                ...(hasSelection ? {} : { '& .MuiAutocomplete-clearIndicator': { display: 'none' } }),
              }
            : undefined
        }
        slotProps={{
          popper: {
            style: {
              width: popupWidth,
              ...(popperZIndex !== undefined ? { zIndex: popperZIndex } : {}),
            },
          },
          ...(multiple ? { listbox: { 'aria-multiselectable': true } as HTMLAttributes<HTMLElement> } : {}),
        }}
        renderOption={(optionProps, row) => {
          const { key, ...liProps } = optionProps as { key?: string } & typeof optionProps;
          const state = stateOf(row);
          const tri = state === 'none' ? 'none' : state === 'both' || !row.sandbox ? 'all' : 'some';
          return (
            <Box
              component="li"
              key={key ?? row.key}
              {...liProps}
              aria-label={rowAriaName(row, state, multiple)}
              sx={{
                gap: 1.25,
                fontSize: 14,
                minWidth: 0,
                pl: row.depth === 1 ? 4 : undefined,
                fontStyle: row.sentinel ? 'italic' : undefined,
                ...(multiple
                  ? {
                      // The checkbox says what is picked; no selected tint on top.
                      '&&&[aria-selected="true"]': { bgcolor: 'transparent' },
                      '&&&[aria-selected="true"].Mui-focused': { bgcolor: 'action.hover' },
                    }
                  : {}),
              }}
            >
              {multiple && <TriState state={tri} />}
              <Box
                component="span"
                sx={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.75 }}
              >
                {row.depth === 1 && (
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    ↳
                  </Box>
                )}
                {/* Full names, no ellipsis — long ones wrap onto the next line. */}
                <Box component="span" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {row.name}
                </Box>
                {row.code && (
                  <Box component="span" sx={{ fontFamily: MONO, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {row.code}
                  </Box>
                )}
                {row.depth === 0 && row.isFranchise && (
                  <Box component="span" sx={captionSx}>
                    franchise
                  </Box>
                )}
                {row.depth === 1 && row.parentName && (
                  <Box component="span" sx={captionSx}>
                    of {row.parentName}
                  </Box>
                )}
                {row.standaloneSandbox && <SandboxMark />}
                {row.closed && <ClosedMark />}
                {row.note && (
                  <Box component="span" sx={captionSx}>
                    {row.note}
                  </Box>
                )}
              </Box>
              {row.sandbox && (
                <Segments
                  envs={multiple ? ['production', 'sandbox', 'both'] : ['production', 'sandbox']}
                  current={state}
                  onPick={(env) => onSegment(row, env)}
                />
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={hasSelection ? undefined : placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: multiple
                ? summaryNode && (
                    <>
                      {summaryNode}
                      {/* The whole selection, for the input's description (the line is cut). */}
                      <Box component="span" id={summaryId} sx={visuallyHidden}>
                        {summaryText}
                      </Box>
                    </>
                  )
                : valueIsSandbox && !searching
                  ? <ScienceOutlinedIcon aria-hidden fontSize="small" sx={{ color: 'text.secondary', ml: 0.5 }} />
                  : undefined,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              ...(describedBy ? { 'aria-describedby': describedBy } : {}),
            }}
          />
        )}
      />
    </FooterContext.Provider>
  );
}
